import { RELOAD_CAL_MODAL } from '@/constants';
import { Button, Dialog, Icon } from '@/ui';

import Cal from './embed';
import ProviderAwareSessionAction from './providerAwareSessionAction';

import type { Session } from '@/types';
import type { FC } from 'react';

import { MemberRescheduleSession } from '@/components/MentoCal/MemberRescheduleSession';
import { useGetSessionQuery } from '@/store/api/sessionApi';
import { sessionActionRoute } from '@/utils/sessionActionRoute';

interface RescheduleSessionProps {
  close?: () => void;
  enableNativeMoments?: boolean;
  onRescheduled?: () => void;
  session: Session;
}

const RescheduleSession: FC<RescheduleSessionProps> = ({
  enableNativeMoments = false,
  onRescheduled = undefined,
  session: sessionProp,
  close = undefined,
}) => {
  // Only refetch if bookingSource is missing from the prop — avoids an
  // extra API call for Cal.com sessions that already carry the field.
  const needsFresh =
    !sessionProp.sourceAppointmentId &&
    !sessionProp.bookingSource &&
    !sessionProp.bookId.startsWith('moments:');
  const { data: freshSession, isLoading: isSessionLoading } =
    useGetSessionQuery(sessionProp.id, {
      skip: !sessionProp.id || !needsFresh,
    });

  const session =
    needsFresh && freshSession?.id === sessionProp.id
      ? freshSession
      : sessionProp;
  const route = sessionActionRoute({
    action: 'reschedule',
    session,
    viewer: enableNativeMoments ? 'owning-member' : 'other',
  });
  const onClose = () => {
    if (!close) {
      setTimeout(() => window.location.reload(), RELOAD_CAL_MODAL);
    } else {
      close();
    }
  };

  // While refetching to determine booking source, don't render Cal.com —
  // a Moments session would briefly show the wrong UI.
  if (needsFresh && isSessionLoading) {
    return null;
  }

  if (route === 'mento-member') {
    return (
      <MemberRescheduleSession
        key={session.sourceAppointmentId ?? session.id}
        close={onClose}
        session={session}
        onRescheduled={onRescheduled}
      />
    );
  }

  if (route !== 'cal') {
    return (
      <ProviderAwareSessionAction
        action="reschedule"
        close={onClose}
        session={session}
      />
    );
  }

  return (
    <Dialog fullScreen open>
      <Button className="absolute right-4 top-4 z-10" onClick={onClose}>
        <Icon>close</Icon>
      </Button>
      <Cal calLink={`reschedule/${session.bookId}`} />
    </Dialog>
  );
};

export default RescheduleSession;
